import { useEffect, useRef, useState } from 'react';
import { loadDocsApi } from '../services/onlyofficeLoader';
import { authApi } from '../services/authApi';

export interface OnlyOfficeViewerHandle {
  scrollToComment(docCommentId: string): void;
}

interface Props {
  versionId: number;
  onReady?: (api: OnlyOfficeViewerHandle) => void;
  /** Called when ONLYOFFICE isn't usable (not configured, or config/script load failed).
   *  The page should fall back to the legacy renderer instead of showing an error. */
  onUnavailable?: () => void;
  className?: string;
}

export function OnlyOfficeDraftViewer({ versionId, onReady, onUnavailable, className }: Props) {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);
  const connectorRef = useRef<any>(null);
  const onReadyRef = useRef(onReady);
  const onUnavailableRef = useRef(onUnavailable);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { onReadyRef.current = onReady; });
  useEffect(() => { onUnavailableRef.current = onUnavailable; });

  useEffect(() => {
    let cancelled = false;
    const placeholderId = `onlyoffice-${versionId}`;

    (async () => {
      try {
        const { server_url, config } = await authApi.getViewerConfig(versionId);
        if (cancelled) return;
        // ONLYOFFICE not configured in this environment (e.g. prod without a
        // Document Server) → fall back to the legacy renderer, don't error.
        if (!server_url) {
          if (onUnavailableRef.current) { onUnavailableRef.current(); return; }
          throw new Error('viewer not configured');
        }
        await loadDocsApi(server_url);
        if (cancelled || !placeholderRef.current) return;
        placeholderRef.current.id = placeholderId;

        const DocsAPI = (window as any).DocsAPI;
        editorRef.current = new DocsAPI.DocEditor(placeholderId, {
          ...config,
          width: '100%',
          height: '100%',
          events: {
            onDocumentReady: () => {
              connectorRef.current = editorRef.current.createConnector?.();
              onReadyRef.current?.({
                scrollToComment: (docCommentId: string) => {
                  if (!docCommentId || docCommentId.startsWith('tc_')) return; // synthetic track-change ids are not navigable
                  // Navigate to the Word comment by id via the connector.
                  // VERIFY exact method against the pinned 9.4 connector API (Task 10).
                  connectorRef.current?.executeMethod?.('ShowComment', [docCommentId]);
                },
              });
            },
          },
        });
      } catch (e: any) {
        if (cancelled) return;
        // Prefer graceful fallback to the legacy renderer over an error state.
        if (onUnavailableRef.current) { onUnavailableRef.current(); return; }
        setError(e?.message ?? 'Failed to load viewer');
      }
    })();

    return () => {
      cancelled = true;
      try { editorRef.current?.destroyEditor?.(); } catch { /* noop */ }
      editorRef.current = null;
      connectorRef.current = null;
    };
  }, [versionId]); // onReady held in a ref (onReadyRef) so callers may pass an inline handler without remounting

  if (error) {
    return <div className={className}><p className="text-sm text-red-600">Viewer unavailable: {error}</p></div>;
  }
  // Fill the viewport below the page headers (editor mounts ~165px from the top),
  // so the editor isn't capped at a short fixed height with empty space below it.
  return <div className={className} style={{ height: 'calc(100vh - 170px)', minHeight: 480 }}><div ref={placeholderRef} style={{ height: '100%' }} /></div>;
}
